import ModifyTouch from 'ol/interaction/Modify'
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { Store, select } from '@ngrx/store'
import { fromJS } from 'immutable'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import View from 'ol/View'
import GeoJSON from 'ol/format/GeoJSON'
import { Geometry, MultiPolygon, Polygon } from 'ol/geom'
import Point from 'ol/geom/Point'
import { Draw, Modify, Select, Snap } from 'ol/interaction'
import { Image as ImageLayer, Vector as VectorLayer } from 'ol/layer'
import { TileWMS, ImageWMS, Vector as VectorSource } from 'ol/source'
import { Fill, Icon, Stroke, Style } from 'ol/style'
import { Subscription } from 'rxjs'
import { getSettingsState } from './../../containers/settings/settings.selectors'
import * as Extent from 'ol/extent'
import Bar from 'ol-ext/control/Bar'
import Toggle from 'ol-ext/control/Toggle'
import Button from 'ol-ext/control/Button'
import DrawHole from 'ol-ext/interaction/DrawHole'
import { getActiveLayers } from '@shared/shared.select'
import { environment } from '@env/environment'
import { addUniqueCoordinates, calculateArea } from '@shared/shared.utils'
import LayerPopup from 'ol-ext/control/LayerPopup'
import { Overlay } from 'ol'
import FillPattern from 'ol-ext/style/FillPattern'
import Control from 'ol/control/Control'

@Component({
  selector: 'measure-map',
  templateUrl: './measure-map.component.html',
  styleUrls: ['./measure-map.component.css'],
})
export class MeasureMapComponent implements OnInit, OnChanges, OnDestroy {
  @Output() polygonChange: EventEmitter<any> = new EventEmitter()
  @Output() markerChange: EventEmitter<any> = new EventEmitter()
  @Input() public lng: number
  @Input() public lat: number
  @Input() public polygon: any
  @Input() public isReadonly = false
  @Input() public canPinEdit: boolean
  @Input() public canPolygonEdit: boolean
  @Input() public layerFeatures: any[]
  public map: Map
  public draw: Draw
  public _polygonFeature: Draw
  public vector: VectorLayer<any>
  public snap: Snap
  public raster: ImageLayer<any>
  public pointFeature: Feature
  public modify: Modify
  public position: number[] = []
  public onDrawStart$: any
  public onDrawEnd$: any
  public onPolygonModify$: any
  private iconStyle = new Style({
    image: new Icon({
      anchor: [0.5, 50],
      anchorXUnits: 'fraction',
      anchorYUnits: 'pixels',
      src: 'https://i.imgur.com/48Uy373.png',
    }),
  })
  public settings: any
  private settings$: Subscription
  public selectionMode: number //0 = Marker 1 = Polygon
  public radioOptions = []
  private source: VectorSource
  private polygonFeature: Feature<Geometry>
  public centroid: any
  public selectCtrl: Toggle
  public editor: Bar
  public mainbar: Bar
  public selectInteraction: Select
  public removeCtrl: Button
  public geojson: string
  public selectedFeature: Feature
  public drawHole: DrawHole
  private activeLayersState$: Subscription
  public activeLayers = []
  public isPopupVisible: boolean
  public popupData: any = {}
  public popupDataKeys = []
  public overlay: Overlay
  private interval: any
  private layerPopup: LayerPopup
  public isCoordinatesPopupVisible = false

  @ViewChild('olPopup', { static: false }) popup: ElementRef

  constructor(public store: Store<any>) {}

  ngAfterViewInit() {
    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.settings = state.get('data')
        if (this.settings.size && !this.map) {
          this.initMap()
        }
      })
  }

  ngOnInit() {
    const options = []

    if (this.canPolygonEdit) {
      options.push({
        label: 'PLOT',
        value: 1,
      })

      this.selectionMode = 1
    }

    if (this.canPinEdit) {
      options.push({
        label: 'PIN',
        value: 0,
      })

      this.selectionMode = 0
    }

    this.radioOptions = options

    const lngLat = [this.lng, this.lat]

    if (this.lat && this.lng && !fromJS(lngLat).equals(fromJS(this.position))) {
      this.position = lngLat
    }

    this.activeLayersState$ = this.store
      .pipe(select(getActiveLayers))
      .subscribe((state) => {
        if (this.activeLayers.length === 0) {
          this.activeLayers = state.toJS()
          this.interval = setInterval(() => {
            if (this.map) {
              this.addLayersToMap()

              clearInterval(this.interval)
            }
          })
        }
      })
  }

  ngOnDestroy() {
    this.settings$.unsubscribe()

    if (this.activeLayersState$) {
      this.activeLayersState$.unsubscribe()
    }

    if (this.layerPopup) {
      this.layerPopup.un('layer:visible', this.markSelectedLayers.bind(this))
    }
  }

  ngOnChanges(changes: any) {
    const lngLat = [this.lng, this.lat]

    if (
      JSON.stringify(changes.layerFeatures.previousValue) !==
        JSON.stringify(changes.layerFeatures.currentValue) &&
      this.map
    ) {
      this.markSelectedLayers()
    }

    if (
      this.lat &&
      this.lng &&
      !fromJS(lngLat).equals(fromJS(this.position)) &&
      this.map
    ) {
      this.position = lngLat
      this.updatePointFeature()
    }
  }

  addCoordinatesFinderButton(): void {
    const button = document.createElement('button')
    const icon = document.createElement('img')
    icon.setAttribute('src', '/assets/search-icon.svg')
    button.appendChild(icon)
    button.addEventListener('click', () => {
      this.isCoordinatesPopupVisible = !this.isCoordinatesPopupVisible
    })
    button.className = 'coordinates-button'

    const controlDiv = document.createElement('div')
    controlDiv.className = 'coordinates-button-control'
    controlDiv.appendChild(button)

    const controlElement = document.createElement('div')
    controlElement.className = 'ol-unselectable ol-control coordinates-control'
    controlElement.appendChild(controlDiv)

    const mapControls = this.map.getControls()
    mapControls.push(
      new (class extends Control {
        constructor() {
          super({
            element: controlElement,
          })
        }
      })(),
    )
  }

  updatePointFeature() {
    if (this.pointFeature) {
      this.source.removeFeature(this.pointFeature)
    }

    if (this.position[0] && this.position[1]) {
      this.pointFeature = new Feature({
        geometry: new Point(this.position),
      })
      this.pointFeature.setStyle(this.iconStyle)
      this.source.addFeature(this.pointFeature)
    }
  }

  selectionModeChange(mode: number) {
    this.selectionMode = mode

    if (mode == 1) {
      this.addEditableControls()
    } else {
      this.removeEditableControls()
      this.closeEdit()
    }
  }

  pinMarker({ coordinate }) {
    if (this.selectionMode != 0 || this.isReadonly) return false
    const position = coordinate
    this.markerChange.emit(position)
  }

  setOpacity = (hex: string, alpha: number) =>
    hex
      ? `${hex}${Math.floor(alpha * 255)
          .toString(16)
          .padStart(2)}`
      : null

  addLayersToMap() {
    const _this = this

    this.activeLayers.forEach(({ id, color, name }) => {
      const source = new VectorSource({
        format: new GeoJSON(),
        url: `${environment.API_ENDPOINT}/gis/public/layer/${id}`,
      })

      const styles = {
        MultiPolygon: new Style({
          stroke: new Stroke({
            color: _this.setOpacity(color, 0.5),
            width: 1,
          }),
          fill: new Fill({
            color: _this.setOpacity(color, 0.3),
          }),
        }),
        Polygon: new Style({
          stroke: new Stroke({
            color: _this.setOpacity(color, 0.5),
            width: 1,
          }),
          fill: new Fill({
            color: _this.setOpacity(color, 0.3),
          }),
        }),
      }

      const vector = new VectorLayer({
        properties: {
          layerId: id,
          name,
        },
        visible: false,
        source,
        style: function (feature: any) {
          return styles[feature.getGeometry().getType()]
        },
      })

      _this.map.addLayer(vector)
    })

    this.layerPopup = new LayerPopup()

    _this.map.addControl(this.layerPopup)

    this.layerPopup.on('layer:visible', this.markSelectedLayers.bind(this))
  }

  clearSelectedLayerFeature() {
    if (this.map) {
      this.map.getLayers().forEach((mapLayer) => {
        const properties = mapLayer.getPropertiesInternal()
        const mapLayerId = properties?.layerId
        const source = properties.source as VectorSource
        const mark = () => {
          if (source.getFeatures) {
            source.getFeatures().forEach((feature) => {
              const props = feature.getPropertiesInternal()
              const activeLayer = this.activeLayers.find(
                ({ id }) => id === mapLayerId,
              )

              if (props.highlighted) {
                feature.setProperties({
                  ...feature.getPropertiesInternal(),
                  highlighted: false,
                })
                feature.setStyle(
                  new Style({
                    stroke: new Stroke({
                      color: this.setOpacity(activeLayer.color, 0.5),
                      width: 1,
                    }),
                    fill: new Fill({
                      color: this.setOpacity(activeLayer.color, 0.3),
                    }),
                  }),
                )
              }
            })
          }
        }

        if (mapLayer.getVisible() && source.getFeatures) {
          if (source.getFeatures().length) {
            mark()
          } else {
            source.on('featuresloadend', mark)
          }
        }
      })
    }
  }

  markSelectedLayers() {
    this.clearSelectedLayerFeature()

    this.layerFeatures.forEach(({ layerFeature, layerId, id: featureId }) => {
      this.map.getLayers().forEach((mapLayer) => {
        const properties = mapLayer.getPropertiesInternal()
        const mapLayerId = properties?.layerId
        const source = properties.source as VectorSource
        const mark = () => {
          source.getFeatures().forEach((feature) => {
            const props = feature.getPropertiesInternal()
            const activeLayer = this.activeLayers.find(
              ({ id }) => id === mapLayerId,
            )

            if (props.highlighted) {
              feature.setProperties({
                ...feature.getPropertiesInternal(),
                highlighted: false,
              })
              feature.setStyle(
                new Style({
                  stroke: new Stroke({
                    color: this.setOpacity(activeLayer.color, 0.5),
                    width: 1,
                  }),
                  fill: new Fill({
                    color: this.setOpacity(activeLayer.color, 0.3),
                  }),
                }),
              )
            }

            if (
              layerFeature
                ? props.id === layerFeature.id
                : props.id === featureId
            ) {
              feature.setProperties({
                ...feature.getPropertiesInternal(),
                highlighted: true,
              })
              feature.setStyle(
                new Style({
                  stroke: new Stroke({
                    color: this.setOpacity(activeLayer.color, 0.5),
                    width: 1,
                  }),
                  fill: new FillPattern({
                    pattern: 'hatch',
                    color: activeLayer.color,
                    scale: 1,
                    size: 1,
                    spacing: 3,
                    angle: 45,
                    fill: new Fill({
                      color: this.setOpacity(activeLayer.color, 0.5),
                    }),
                  }),
                }),
              )
            }
          })

          source.un('featuresloadend', mark)
        }

        if (mapLayer.getVisible()) {
          if (
            layerFeature
              ? mapLayerId === layerFeature.layerId
              : mapLayerId === layerId
          ) {
            if (source.getFeatures().length) {
              mark()
            } else {
              source.on('featuresloadend', mark)
            }
          }
        }
      })
    })
  }

  initMap() {
    const {
      GIS_GEOSERVER_URL,
      GIS_GEOSERVER_WORKSPACE,
      GIS_BASE_LAYER,
      GIS_MAP_EXTENT,
    } = this.settings.toJS().gis
    const extent = GIS_MAP_EXTENT.split(',').map((c: string) => parseFloat(c))
    const origin = extent[0] + ',' + extent[1]
    const features: any[] = []

    this.overlay = new Overlay({
      element: this.popup.nativeElement,
      offset: [-128, 20],
      autoPan: false,
    })

    this.raster = new ImageLayer({
      source: new ImageWMS({
        params: {
          FORMAT: 'image/jpeg',
          VERSION: '1.1.1',
          // tiled: true,
          LAYERS: `${GIS_GEOSERVER_WORKSPACE}:${GIS_BASE_LAYER}`,
          exceptions: 'application/vnd.ogc.se_inimage',
          tilesOrigin: origin,
        },
        url: `${GIS_GEOSERVER_URL}/${GIS_GEOSERVER_WORKSPACE}/wms`,
      }),
    })

    if (this.position.length) {
      this.pointFeature = new Feature({
        geometry: new Point(this.position),
      })

      this.pointFeature.setStyle(this.iconStyle)
      features.push(this.pointFeature)
    }

    if (this.polygon) {
      let geometry: any

      if (this.polygon.type === 'MultiPolygon') {
        geometry = new MultiPolygon(this.polygon.coordinates)
      } else if (this.polygon.type === 'Polygon') {
        geometry = new Polygon(this.polygon.coordinates)
      }

      if (geometry) {
        const feature = new Feature({
          geometry: geometry,
          selectable: true,
        })
        this.polygonFeature = feature
        features.push(this.polygonFeature)

        if (this.polygon.coordinates.length) {
          this.centroid = Extent.getCenter(geometry.getExtent())
        }
      }
    } else if (this.pointFeature) {
      this.centroid = Extent.getCenter(
        this.pointFeature.getGeometry().getExtent(),
      )
    }

    const source = new VectorSource({
      features,
    })

    this.source = source

    this.vector = new VectorLayer({
      source,
      zIndex: 100,
    })

    this.map = new Map({
      layers: [this.raster, this.vector],
      overlays: [this.overlay],
      target: 'ol-map',
      view: new View({
        projection: 'EPSG:4326',
        center: this.centroid
          ? this.centroid
          : [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
        zoom: this.polygonFeature || this.pointFeature ? 20 : 15,
      }),
    })

    this.map.on('pointermove', ({ pixel }) => {
      const feature = this.map.forEachFeatureAtPixel(pixel, function (feature) {
        return feature
      }) as any

      if (feature) {
        const geometry = feature.getGeometry() as any
        const coordinates = geometry.getCoordinates()
        let newCoordinates: any[]

        const { label, value } = feature.values_

        if (!!label || !!value) {
          this.popupData = {
            label,
            value,
          }
          this.popupDataKeys = Object.keys(this.popupData)
          this.isPopupVisible = true

          if (coordinates.length === 1) {
            var centroid = Extent.getCenter(geometry.getExtent())
            newCoordinates = centroid
          } else {
            newCoordinates = coordinates
          }

          this.overlay.setPosition(newCoordinates)
        } else {
          this.isPopupVisible = false
        }
      } else {
        this.isPopupVisible = false
      }
    })

    this.addControls()

    this.snap = new Snap({ source })
    this.modify = new Modify({ source })

    if (!this.isReadonly && this.selectionMode == 1) {
      this.map.addInteraction(this.modify)
      this.map.addInteraction(this.draw)
    }
    this.map.addInteraction(this.snap)
    this.map.on('click', this.pinMarker.bind(this))
  }

  addControls() {
    const _this = this
    this.mainbar = new Bar({
      toggleOne: true,
      group: true,
      className: 'ol-controls',
    })

    this.map.addControl(this.mainbar)

    this.editor = new Bar({
      toggleOne: true,
      group: false,
    })
    this.mainbar.addControl(this.editor)

    const selectBar = new Bar()

    this.selectCtrl = new Toggle({
      html: '<i class="fa-solid fa-arrow-pointer"></i>',
      title: 'Select',
      interaction: new Select({
        hitTolerance: 2,
        condition: function (event) {
          if (event.type === 'singleclick') {
            const selectable = _this.map.forEachFeatureAtPixel(
              event.pixel,
              function (e: Feature) {
                return e.getProperties().selectable && _this.selectionMode == 1
              },
            )

            if (!selectable) {
              _this.closeEdit()
            }

            return selectable
          }
          return false
        },
      }),
      bar: selectBar,
      autoActive: true,
      active: true,
    })

    this.editor.addControl(this.selectCtrl)

    this.removeCtrl = new Button({
      html: '<i class="fa fa-times"></i>',
      handleClick: function () {
        if (_this.selectedFeature) {
          _this.selectInteraction.getFeatures().remove(_this.selectedFeature)
          _this.vector.getSource().removeFeature(_this.selectedFeature)
          _this.vector.getSource().changed()
          _this.selectedFeature.setGeometry(new MultiPolygon([]))
        }
      },
    })

    this.editor.addControl(this.removeCtrl)

    this.removeCtrl.getButtonElement().setAttribute('disabled', 'true')

    const polygon = new Toggle({
      html: '<i class="fa-solid fa-draw-polygon"></i>',
      disable: true,
      interaction: new Draw({
        type: 'MultiPolygon',
        source: _this.vector.getSource(),
      }),
    })

    polygon.set('selectable', true)

    this.editor.addControl(polygon)

    const drawhole = new Toggle({
      disable: true,
      html: '<i class="fa-solid fa-scissors"></i>',
      interaction: new DrawHole({
        type: 'MultiPolygon',
        layers: [this.vector],
      }),
    })

    this.editor.addControl(drawhole)

    this.draw = polygon.getInteraction() as Draw
    this.drawHole = drawhole.getInteraction() as DrawHole
    this.selectInteraction = this.selectCtrl.getInteraction() as Select

    this.draw.on('drawend', this.onDrawEnd.bind(this))
    this.drawHole.on('drawend', this.onDrawHoleEnd.bind(this))
    this.selectInteraction.on('select', this.onSelect.bind(this))

    this.addCoordinatesFinderButton()
  }

  onDrawHoleEnd({ feature, type, features }) {
    if (type === 'drawend') {
      this.geojson = new GeoJSON().writeGeometry(feature.getGeometry())
    }
  }

  onDrawEnd({ feature, type, features }) {
    if (type === 'drawend') {
      if (feature && this.selectedFeature) {
        const geometry = feature.getGeometry()
        const newCoordinates = geometry.getCoordinates()
        const existingFeature = this.selectedFeature.clone()
        const existingGeometry =
          existingFeature.getGeometry() instanceof MultiPolygon
            ? (existingFeature.getGeometry() as MultiPolygon)
            : new MultiPolygon([])
        const existingCoordinates = existingGeometry.getCoordinates()

        // Only add coordinates if they are different from existing ones
        addUniqueCoordinates(existingCoordinates, newCoordinates)

        existingGeometry.setCoordinates(existingCoordinates)

        feature.setGeometry(existingGeometry)
        feature.setId(this.selectedFeature.getId())
        feature.set('selectable', true)
        feature.changed()

        this.vector.getSource().removeFeature(this.selectedFeature)
        this.vector.getSource().changed()

        this.selectInteraction.getFeatures().clear()
        this.selectInteraction.getFeatures().push(feature)
        this.selectInteraction.changed()

        this.selectedFeature = feature
      } else {
        this.selectedFeature = feature
      }
    }

    if (type === 'modifyend') {
      const selectedFeature = features.pop() as Feature

      if (selectedFeature) {
        this.selectedFeature.setGeometry(selectedFeature.getGeometry())
      }
    }

    this.polygonChange.emit(
      this.getPolygonAndArea(this.selectedFeature.getGeometry()),
    )
  }

  addEditableControls() {
    this.modify = new ModifyTouch({
      source: this.vector.getSource(),
      features: (this.selectCtrl.getInteraction() as Select).getFeatures(),
    })

    this.map.addInteraction(this.modify)
    this.modify.on('modifyend', this.onDrawEnd.bind(this))

    this.editor.getControls().forEach((c: any) => {
      if (c.getInteraction && c.getInteraction() instanceof Draw) {
        c.setDisable(false)
      }
    })
  }

  removeEditableControls() {
    this.map.removeInteraction(this.modify)

    this.editor.getControls().forEach((c: any) => {
      if (c.getInteraction && c.getInteraction() instanceof Draw) {
        c.setDisable(true)
      }
    })
  }

  openEdit(feature: Feature) {
    this.selectedFeature = feature

    this.addEditableControls()
    this.removeCtrl.getButtonElement().removeAttribute('disabled')
  }

  closeEdit() {
    if (!this.selectedFeature) return

    this.selectInteraction.getFeatures().remove(this.selectedFeature)

    this.removeEditableControls()
    this.removeCtrl.getButtonElement().setAttribute('disabled', 'true')
    this.selectCtrl.setActive(true)

    this.selectedFeature = null
  }

  onSelect({ selected }) {
    if (selected.length) {
      const feature = selected[0]
      this.openEdit(feature)
    } else {
      this.closeEdit()
    }
  }

  // getArea(geometry: any) {
  //   const areaInMeters = getArea(geometry)
  //   const areaInFt = areaInMeters * 10.764
  //   return areaInFt
  // }

  getPolygon(geometry: any) {
    return JSON.parse(new GeoJSON().writeGeometry(geometry))
  }

  getPolygonAndArea(geometry: any) {
    const polygon = this.getPolygon(geometry)
    const area = calculateArea(geometry, this.settings.get('units').toJS())

    return {
      polygon,
      area,
    }
  }
}
